// ===========================================
// CURRENCY COMPONENT
// ===========================================
// Renders virtual play-chip ("B-Bucks") amounts with the "B-Bucks" label in
// JetBrains Mono while keeping the number in the inherited font.
//
// NOTE: chips are play money with NO cash value. We deliberately use the product
// NAME ("B-Bucks", cf. Fortnite "V-Bucks"), never a real-world/sovereign currency
// symbol (฿, $, €), because a currency sign implies monetary value and undermines
// the play-money positioning.

import { formatCurrency } from '../utils/format';

interface CurrencyProps {
  /** The amount to display */
  amount: number | string;
  /** Whether to show the B-Bucks label (default: true) */
  showSymbol?: boolean;
  /** Additional className for the container */
  className?: string;
  /** Whether to show + prefix for positive amounts */
  showSign?: boolean;
  /** Color class for positive amounts (when showSign is true) */
  positiveClass?: string;
  /** Color class for negative amounts */
  negativeClass?: string;
}

/**
 * The "B-Bucks" play-chip label, styled with JetBrains Mono.
 * Use this component anywhere you need the chip label. Deliberately the product
 * NAME, not a currency symbol (cf. Fortnite "V-Bucks") — chips are play money with
 * no cash value, so we never denominate them with a currency sign.
 */
export function BahtSymbol({ className = '' }: { className?: string }) {
  return (
    <span
      className={className}
      style={{ fontFamily: '"JetBrains Mono", monospace', fontWeight: 'normal' }}
    >
      {'B-Bucks '}
    </span>
  );
}

/**
 * Play-chip display component.
 * Renders the "B-Bucks" label in JetBrains Mono while keeping numbers in inherited font.
 *
 * @example
 * <Currency amount={1000} />                    // B-Bucks 1,000
 * <Currency amount={-500} negativeClass="text-red-400" />  // B-Bucks -500 (red)
 * <Currency amount={100} showSign />            // +B-Bucks 100
 * <Currency amount={1000} showSymbol={false} /> // 1,000
 */
export function Currency({
  amount,
  showSymbol = true,
  className = '',
  showSign = false,
  positiveClass = 'text-green-400',
  negativeClass = 'text-red-400',
}: CurrencyProps) {
  const num = typeof amount === 'string' ? parseFloat(amount) : amount;
  const isNegative = num < 0;
  const isPositive = num > 0;
  const absAmount = Math.abs(num);
  const formatted = formatCurrency(absAmount);

  // Determine color class based on sign
  let colorClass = '';
  if (showSign || isNegative) {
    colorClass = isNegative ? negativeClass : (isPositive ? positiveClass : '');
  }

  // Build the sign prefix
  let signPrefix = '';
  if (isNegative) {
    signPrefix = '-';
  } else if (showSign && isPositive) {
    signPrefix = '+';
  }

  return (
    <span className={`${className} ${colorClass}`.trim()}>
      {signPrefix}
      {showSymbol && <BahtSymbol />}
      {formatted}
    </span>
  );
}

export default Currency;
